/**
 * Cores para badges e bolinhas do Funil IA.
 * Com várias campanhas visíveis, usa ordem na lista (ids ordenados) para que cada uma
 * receba um tom distinto; leads sem campanha na lista caem no fallback por hash.
 */

export type FunilCampaignSwatch = {
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  circleFrom: string;
  circleTo: string;
  circleShadow: string;
  thinkingGlow: string;
};

/** Paleta fixa: fundos claros no badge, gradientes legíveis com texto branco na bolinha. */
const SWATCHES: FunilCampaignSwatch[] = [
  {
    badgeBg: 'rgba(219, 234, 254, 0.95)',
    badgeBorder: '#93c5fd',
    badgeText: '#1e3a8a',
    circleFrom: '#3b82f6',
    circleTo: '#1d4ed8',
    circleShadow: 'rgba(37, 99, 235, 0.38)',
    thinkingGlow: 'rgba(37, 99, 235, 0.5)',
  },
  {
    badgeBg: 'rgba(254, 249, 195, 0.95)',
    badgeBorder: '#facc15',
    badgeText: '#713f12',
    circleFrom: '#eab308',
    circleTo: '#ca8a04',
    circleShadow: 'rgba(202, 138, 4, 0.4)',
    thinkingGlow: 'rgba(234, 179, 8, 0.55)',
  },
  {
    badgeBg: 'rgba(220, 252, 231, 0.95)',
    badgeBorder: '#6ee7b7',
    badgeText: '#065f46',
    circleFrom: '#10b981',
    circleTo: '#047857',
    circleShadow: 'rgba(5, 150, 105, 0.38)',
    thinkingGlow: 'rgba(16, 185, 129, 0.5)',
  },
  {
    badgeBg: 'rgba(243, 232, 255, 0.95)',
    badgeBorder: '#c4b5fd',
    badgeText: '#5b21b6',
    circleFrom: '#8b5cf6',
    circleTo: '#6d28d9',
    circleShadow: 'rgba(109, 40, 217, 0.38)',
    thinkingGlow: 'rgba(139, 92, 246, 0.5)',
  },
  {
    badgeBg: 'rgba(255, 237, 213, 0.95)',
    badgeBorder: '#fdba74',
    badgeText: '#9a3412',
    circleFrom: '#f97316',
    circleTo: '#c2410c',
    circleShadow: 'rgba(194, 65, 12, 0.38)',
    thinkingGlow: 'rgba(249, 115, 22, 0.5)',
  },
  {
    badgeBg: 'rgba(224, 242, 254, 0.95)',
    badgeBorder: '#7dd3fc',
    badgeText: '#0c4a6e',
    circleFrom: '#0ea5e9',
    circleTo: '#0369a1',
    circleShadow: 'rgba(3, 105, 161, 0.38)',
    thinkingGlow: 'rgba(14, 165, 233, 0.5)',
  },
  {
    badgeBg: 'rgba(252, 231, 243, 0.95)',
    badgeBorder: '#f9a8d4',
    badgeText: '#831843',
    circleFrom: '#ec4899',
    circleTo: '#be185d',
    circleShadow: 'rgba(190, 24, 93, 0.38)',
    thinkingGlow: 'rgba(236, 72, 153, 0.5)',
  },
  {
    badgeBg: 'rgba(229, 231, 235, 0.95)',
    badgeBorder: '#9ca3af',
    badgeText: '#1f2937',
    circleFrom: '#64748b',
    circleTo: '#334155',
    circleShadow: 'rgba(51, 65, 85, 0.38)',
    thinkingGlow: 'rgba(100, 116, 139, 0.5)',
  },
];

function hash32(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function swatchIndexForCampaignId(campaignId: string): number {
  if (!campaignId.trim()) return 0;
  return hash32(campaignId) % SWATCHES.length;
}

/** Fallback quando o id não está na lista atual (ex.: lead órfão). */
export function funilCampaignSwatch(campaignId: string): FunilCampaignSwatch {
  return SWATCHES[swatchIndexForCampaignId(campaignId)] ?? SWATCHES[0];
}

/**
 * Cor alinhada à ordem das campanhas na lista (1.ª = 1.º tom, 2.ª = 2.º, …).
 * Evita colisão do hash (duas campanhas com o mesmo índice) e mantém badge e bolinha coerentes com a ordem do ecrã.
 */
export function funilCampaignSwatchForList(
  campaigns: ReadonlyArray<{ campaignId: string }>,
  campaignId: string
): FunilCampaignSwatch {
  const id = campaignId.trim();
  if (!id) return SWATCHES[0];
  const idx = campaigns.findIndex((c) => c.campaignId === id);
  if (idx < 0) {
    return funilCampaignSwatch(id);
  }
  return SWATCHES[idx % SWATCHES.length] ?? SWATCHES[0];
}

export function funilCampaignSwatchCount(): number {
  return SWATCHES.length;
}
